/**
 * Volunteers dashboard section: tabbed list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LayoutGrid } from 'lucide-react';
import { VolunteersDataTable } from '@/components/volunteers/VolunteersDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateVolunteerView } from '@/components/volunteers/CreateVolunteerView';
import { EditVolunteerView } from '@/components/volunteers/EditVolunteerView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { Select } from '@/components/forms/Select';
import { usePageActions } from '@/hooks/usePageActions';
import { useRoleGroupTabs } from '@/hooks/useRoleGroupTabs';
import { DynamicIcon } from '@/components/forms/IconPicker';
import { VOLUNTEER_TAB_COLORS } from '@/components/volunteers/volunteer-configs';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link VolunteersSection}. */
interface VolunteersSectionProps {
  layoutWidth?: ElementWidth;
  onSuccess?: () => void;
}

/** Dashboard widget: volunteers list. */
export const VolunteersSection: React.FC<VolunteersSectionProps> = ({
  layoutWidth = 'full',
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editVolunteerId, setEditVolunteerId] = React.useState<string | null>(null);

  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('volunteers');
  const { openRowId, openRow, closeRow } = useDetailsView();
  const { visibleTabs, showTabBar, activeRoleFilter, setActiveRoleFilter } = useRoleGroupTabs(
    'volunteers',
    'menu-volunteers',
  );

  /** Role-code filter for the table; empty when the All tab is selected. */
  const enabledRoles: string[] = activeRoleFilter === 'all' ? [] : [activeRoleFilter];

  const handleCreateVolunteer = () => {
    setCreateFormOpen(true);
    setEditVolunteerId(null);
  };

  const handleEditVolunteer = (id: string) => {
    setEditVolunteerId(id);
    setCreateFormOpen(false);
  };

  const handleSuccess = () => {
    setCreateFormOpen(false);
    setEditVolunteerId(null);
    void queryClient.invalidateQueries({ queryKey: ['users', 'volunteer'] });
    onSuccess?.();
  };

  const tabsNode = React.useMemo(
    () =>
      showTabBar ? (
        <Select
          value={activeRoleFilter}
          onChange={setActiveRoleFilter}
          size="sm"
          options={[
            {
              value: 'all',
              label: 'Tous',
              icon: <LayoutGrid className="h-4 w-4" />,
              color: VOLUNTEER_TAB_COLORS.all,
            },
            ...visibleTabs.map((tab) => ({
              value: tab.id,
              label: tab.label,
              icon: <DynamicIcon name={tab.icon} className="h-4 w-4" />,
              color: tab.color ?? undefined,
            })),
          ]}
        />
      ) : undefined,
    [showTabBar, activeRoleFilter, setActiveRoleFilter, visibleTabs],
  );

  return (
    <>
      <VolunteersDataTable
        enabledRoles={enabledRoles}
        showCreateButton={canCreate}
        isSection={true}
        layoutWidth={layoutWidth}
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditVolunteer={handleEditVolunteer}
        onCreateVolunteer={handleCreateVolunteer}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
        toolbarLeft={tabsNode}
      />

      <SlidePanel isOpen={createFormOpen} onClose={() => setCreateFormOpen(false)}>
        <CreateVolunteerView onClose={() => setCreateFormOpen(false)} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={editVolunteerId !== null} onClose={() => setEditVolunteerId(null)}>
        {editVolunteerId && (
          <EditVolunteerView
            key={editVolunteerId}
            volunteerId={editVolunteerId}
            onClose={() => setEditVolunteerId(null)}
            onSuccess={handleSuccess}
          />
        )}
      </SlidePanel>
    </>
  );
};
